import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import ClientCombobox from "@/components/ClientCombobox";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Ticket, Plus, Search, AlertTriangle, Clock, CheckCircle2, Pause,
  XCircle, ArrowUpCircle, ArrowRightCircle, ArrowDownCircle, UserX, UserCheck,
} from "lucide-react";
import { canEditModule } from "@/utils/permissions";
import { ModuleHeaderIcon } from "@/components/ModuleHeader";

const STATUS_MAP = {
  abierto: { label: "Abierto", color: "bg-blue-500 text-white", icon: Ticket },
  en_progreso: { label: "En Progreso", color: "bg-amber-500 text-white", icon: Clock },
  en_espera: { label: "En Espera", color: "bg-purple-500 text-white", icon: Pause },
  resuelto: { label: "Resuelto", color: "bg-green-500 text-white", icon: CheckCircle2 },
  cerrado: { label: "Cerrado", color: "bg-slate-500 text-white", icon: XCircle },
};

const PRIORITY_MAP = {
  baja: { label: "Baja", color: "bg-slate-100 text-slate-700", icon: ArrowDownCircle },
  media: { label: "Media", color: "bg-blue-100 text-blue-700", icon: ArrowRightCircle },
  alta: { label: "Alta", color: "bg-orange-100 text-orange-700", icon: ArrowUpCircle },
  urgente: { label: "Urgente", color: "bg-red-100 text-red-700", icon: AlertTriangle },
};

const DEFAULT_CATEGORIES = ["TI", "Mantenimiento", "Traveldesk", "Logística", "Administración"];

const HelpdeskTickets = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [employees, setEmployees] = useState([]);
  const [categorias, setCategorias] = useState(DEFAULT_CATEGORIES);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterAssigned, setFilterAssigned] = useState("all");
  const [showNew, setShowNew] = useState(false);
  const [newTicket, setNewTicket] = useState({
    title: "", description: "", category: "TI", priority: "media", cliente: "",
    assigned_to_id: "", assigned_to_name: "",
  });

  useEffect(() => {
    fetchTickets();
    fetchStats();
    axios.get(`${API}/users-list`, { withCredentials: true }).then(r => setEmployees(r.data)).catch(() => {});
    axios.get(`${API}/ticket-categorias`, { withCredentials: true }).then(r => setCategorias(r.data.map(c => c.nombre))).catch(() => {});
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/tickets`, { withCredentials: true });
      setTickets(res.data);
    } catch (err) {
      toast.error("Error al cargar tickets");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/tickets/stats`, { withCredentials: true });
      setStats(res.data);
    } catch (err) {}
  };

  const handleCreate = async () => {
    if (!newTicket.title.trim()) return toast.error("El título es requerido");
    try {
      await axios.post(`${API}/tickets`, newTicket, { withCredentials: true });
      toast.success("Ticket creado");
      setShowNew(false);
      setNewTicket({ title: "", description: "", category: "TI", priority: "media", cliente: "", assigned_to_id: "", assigned_to_name: "" });
      fetchTickets();
      fetchStats();
    } catch (err) {
      toast.error("Error al crear ticket");
    }
  };

  // Compute extra stats
  const sinAsignar = tickets.filter(t => !t.assigned_to_id && !["resuelto", "cerrado"].includes(t.status)).length;
  const misTickets = tickets.filter(t => t.assigned_to_name === user?.name && !["resuelto", "cerrado"].includes(t.status)).length;

  // Stat card click handler
  const handleStatClick = (status) => {
    setFilterStatus(status);
    setFilterAssigned("all");
    setFilterCategory("all");
  };

  // Apply all filters
  const applyFilters = (list) => list.filter(t => {
    if (filterStatus !== "all" && t.status !== filterStatus) return false;
    if (filterCategory !== "all" && t.category !== filterCategory) return false;
    if (filterAssigned === "sin_asignar" && t.assigned_to_id) return false;
    if (filterAssigned === "mis_tickets" && t.assigned_to_name !== user?.name) return false;
    if (filterAssigned !== "all" && filterAssigned !== "sin_asignar" && filterAssigned !== "mis_tickets" && t.assigned_to_name !== filterAssigned) return false;
    if (search) {
      const s = search.toLowerCase();
      return t.folio?.toLowerCase().includes(s) || t.title?.toLowerCase().includes(s) || t.created_by_name?.toLowerCase().includes(s);
    }
    return true;
  });

  const ticketsSinAsignar = applyFilters(tickets.filter(t => !t.assigned_to_id));
  const ticketsAsignados = applyFilters(tickets.filter(t => !!t.assigned_to_id));

  // Unique assigned names for filter
  const assignedNames = [...new Set(tickets.filter(t => t.assigned_to_name).map(t => t.assigned_to_name))].sort();

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  const TicketTable = ({ data, title, emptyMsg }) => (
    <Card>
      <div className="px-4 py-3 border-b bg-slate-50">
        <h3 className="font-semibold text-slate-700 flex items-center gap-2">
          {title} <Badge variant="outline">{data.length}</Badge>
        </h3>
      </div>
      <div className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>FOLIO</TableHead>
              <TableHead>TÍTULO</TableHead>
              <TableHead>CLIENTE</TableHead>
              <TableHead>CATEGORÍA</TableHead>
              <TableHead>PRIORIDAD</TableHead>
              <TableHead>ESTADO</TableHead>
              <TableHead>ASIGNADO A</TableHead>
              <TableHead>CREADO POR</TableHead>
              <TableHead>FECHA</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-6 text-slate-400">{emptyMsg}</TableCell></TableRow>
            ) : data.map(t => {
              const st = STATUS_MAP[t.status] || STATUS_MAP.abierto;
              const pr = PRIORITY_MAP[t.priority] || PRIORITY_MAP.media;
              return (
                <TableRow key={t.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/helpdesk/tickets/${t.id}`)} data-testid={`ticket-row-${t.folio}`}>
                  <TableCell className="font-mono text-sm font-semibold text-blue-600">{t.folio}</TableCell>
                  <TableCell className="font-medium max-w-xs truncate">{t.title}</TableCell>
                  <TableCell className="text-sm text-slate-700">{t.cliente || <span className="text-slate-300">—</span>}</TableCell>
                  <TableCell><Badge variant="outline">{t.category}</Badge></TableCell>
                  <TableCell><Badge className={pr.color}><pr.icon className="w-3 h-3 mr-1" />{pr.label}</Badge></TableCell>
                  <TableCell><Badge className={st.color}>{st.label}</Badge></TableCell>
                  <TableCell className="text-sm">{t.assigned_to_name || <span className="text-slate-400">Sin asignar</span>}</TableCell>
                  <TableCell className="text-sm">
                    {t.created_by_name}
                    {((t.folio || "").toUpperCase().startsWith("COT-") || t.source === "formulario") && (
                      <Badge variant="outline" className="ml-1 text-[10px] border-blue-300 text-blue-700">COT</Badge>
                    )}
                    {t.source === "formulario_externo" && <Badge variant="outline" className="ml-1 text-[10px]">Link</Badge>}
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">{fmtDate(t.created_at)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </Card>
  );

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="helpdesk-tickets-page">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ModuleHeaderIcon icon={Ticket} />
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Helpdesk — Tickets</h1>
              <p className="text-slate-500">Gestión de solicitudes y soporte</p>
            </div>
          </div>
          {canEditModule(user, "helpdesk_tickets") && (
            <Button onClick={() => setShowNew(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="new-ticket-btn">
              <Plus className="w-4 h-4 mr-2" /> Nuevo Ticket
            </Button>
          )}
        </div>

        {/* Stats - clickable */}
        <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-3">
          {[
            { label: "Total", value: stats.total || 0, color: "text-slate-700", click: () => handleStatClick("all") },
            { label: "Abiertos", value: stats.abiertos || 0, color: "text-blue-600", click: () => handleStatClick("abierto") },
            { label: "En Progreso", value: stats.en_progreso || 0, color: "text-amber-600", click: () => handleStatClick("en_progreso") },
            { label: "En Espera", value: stats.en_espera || 0, color: "text-purple-600", click: () => handleStatClick("en_espera") },
            { label: "Resueltos", value: stats.resueltos || 0, color: "text-green-600", click: () => handleStatClick("resuelto") },
            { label: "Cerrados", value: stats.cerrados || 0, color: "text-slate-500", click: () => handleStatClick("cerrado") },
            { label: "Urgentes", value: stats.urgentes || 0, color: "text-red-600", click: () => { handleStatClick("all"); /* urgente is priority not status */ } },
            { label: "Sin Asignar", value: sinAsignar, color: "text-orange-600", click: () => { setFilterStatus("all"); setFilterAssigned("sin_asignar"); setFilterCategory("all"); } },
            { label: "Mis Tickets", value: misTickets, color: "text-indigo-600", click: () => { setFilterStatus("all"); setFilterAssigned("mis_tickets"); setFilterCategory("all"); } },
          ].map(s => (
            <Card key={s.label} className="cursor-pointer hover:shadow-md transition-shadow" onClick={s.click}>
              <CardContent className="p-3 text-center">
                <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                <p className="text-xs text-slate-500">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Buscar por folio, título o creador..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="ticket-search" />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[160px]" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              {Object.entries(STATUS_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[160px]" data-testid="filter-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las categorías</SelectItem>
              {categorias.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterAssigned} onValueChange={setFilterAssigned}>
            <SelectTrigger className="w-[180px]" data-testid="filter-assigned"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los asignados</SelectItem>
              <SelectItem value="sin_asignar">Sin asignar</SelectItem>
              <SelectItem value="mis_tickets">Mis tickets</SelectItem>
              {assignedNames.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
          {(filterStatus !== "all" || filterCategory !== "all" || filterAssigned !== "all" || search) && (
            <Button variant="ghost" size="sm" onClick={() => { setFilterStatus("all"); setFilterCategory("all"); setFilterAssigned("all"); setSearch(""); }}>
              Limpiar filtros
            </Button>
          )}
        </div>

        {/* Table 1: Sin Asignar */}
        <TicketTable
          data={ticketsSinAsignar}
          title="Tickets Sin Asignar"
          emptyMsg="No hay tickets sin asignar"
        />

        {/* Table 2: Asignados */}
        <TicketTable
          data={ticketsAsignados}
          title="Tickets Asignados"
          emptyMsg="No hay tickets asignados"
        />

        {/* New Ticket Dialog */}
        <Dialog open={showNew} onOpenChange={setShowNew}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Nuevo Ticket</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Título *</Label>
                <Input value={newTicket.title} onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })} placeholder="Describe brevemente el problema o solicitud" data-testid="ticket-title" />
              </div>
              <div className="space-y-2">
                <Label>Descripción</Label>
                <Textarea value={newTicket.description} onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })} placeholder="Detalla el problema, pasos para reproducir, etc." rows={4} data-testid="ticket-description" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Categoría</Label>
                  <Select value={newTicket.category} onValueChange={(v) => setNewTicket({ ...newTicket, category: v })}>
                    <SelectTrigger data-testid="ticket-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {categorias.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Prioridad</Label>
                  <Select value={newTicket.priority} onValueChange={(v) => setNewTicket({ ...newTicket, priority: v })}>
                    <SelectTrigger data-testid="ticket-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(PRIORITY_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Cliente (empresa)</Label>
                <ClientCombobox value={newTicket.cliente || ""} testid="ticket-cliente" placeholder="Empresa relacionada (opcional)"
                  onSelect={(c) => setNewTicket({ ...newTicket, cliente: c.company_name })}
                  onClear={() => setNewTicket({ ...newTicket, cliente: "" })} />
              </div>
              <div className="space-y-2">
                <Label>Asignar a</Label>
                <Select value={newTicket.assigned_to_id || "none"} onValueChange={(v) => {
                  const emp = employees.find(e => e.id === v);
                  setNewTicket({ ...newTicket, assigned_to_id: v === "none" ? "" : v, assigned_to_name: emp?.name || "" });
                }}>
                  <SelectTrigger data-testid="ticket-assigned"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin asignar</SelectItem>
                    {employees.filter(e => e.is_active !== false).map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNew(false)}>Cancelar</Button>
              <Button onClick={handleCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="create-ticket-btn">Crear Ticket</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default HelpdeskTickets;
